import { useEffect, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { ContactForm } from '@/components/result/ContactForm';
import { ButtonLink } from '@/components/ui/Button';
import { getCreatorContent } from '@/content/registry';
import { track } from '@/lib/analytics';
import { useDocumentTitle } from '@/lib/hooks';
import styles from './ContactPage.module.css';

/**
 * Saying hello without doing the questions: /:slug/contact.
 *
 * Until this page existed, the compatibility journey was the only way to reach
 * James at all — the contact form lived on the result page and nowhere else.
 * That quietly turned an unusual, playful thing to do into a toll: seventeen
 * questions from a stranger, or nothing. Somebody who has read the profile and
 * simply wants to suggest a coffee should be able to, and the fact that she did
 * not answer anything is information too.
 *
 * The page is deliberately plain. The whole design of the site pushes towards
 * the questions, and this is the other door rather than a competing offer: no
 * hero, no diagram, a heading, two sentences and the form. The way back to the
 * questions sits underneath it, because a fair number of people will arrive
 * here, read it, and decide they would rather play after all.
 *
 * `ContactForm` is the same component the result page uses. Given no result
 * and no answers it drops the answer-sharing control and stops claiming a
 * result is being sent — see the notes there.
 */
export function ContactPage() {
  const { slug = '' } = useParams();
  const content = getCreatorContent(slug);
  const location = useLocation();
  const announcedRef = useRef(false);

  /*
   * Two very different people land on this page, and telling them apart is the
   * whole reason the field exists: someone who read the profile and chose the
   * quiet link at the bottom of it, and someone who arrived at /:slug/contact
   * cold, from a scanned card or a pasted URL. Reporting both as "direct" made
   * the profile link look like it was never used.
   */
  const from = (location.state as { from?: string } | null)?.from;
  const source = from === 'profile' ? 'profile' : 'direct';

  useDocumentTitle(
    content
      ? `Say hello — ${content.profile.displayName}`
      : 'Not found — Are We Compatible?',
  );

  useEffect(() => {
    if (!content || announcedRef.current) return;
    announcedRef.current = true;
    track({
      name: 'contact_form_opened',
      profileId: content.profile.id,
      category: 'none',
      source,
    });
  }, [content, source]);

  if (!content) return <Navigate to="/404" replace />;

  const { profile } = content;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>No questions required</p>
        <h1 className={styles.title}>Say hello to {profile.displayName}</h1>
        <p className={styles.lede}>
          This goes straight to me. A first name and one way to reach you is
          genuinely all I need. Everything else on this page is optional.
        </p>
      </header>

      <section className={styles.form} aria-label={`Get in touch with ${profile.displayName}`}>
        <ContactForm profile={profile} />
      </section>

      {/*
        A fair number of people will arrive here, read it, and decide they
        would rather play after all — so the way back is a real control rather
        than a link buried in a sentence. On a phone a wrapped inline link is a
        two-line target with a dead gap through the middle of it, which is a
        poor thing to make somebody aim at.
      */}
      <aside className={styles.back}>
        <p>
          Changed your mind? Ten questions, and you see how{' '}
          {profile.displayName} answered each one.
        </p>
        <ButtonLink
          to={`/${profile.slug}/questions`}
          variant="secondary"
          size="small"
          withArrow
        >
          Try the questions instead
        </ButtonLink>
      </aside>
    </div>
  );
}
